import { Palette } from "./palettes";
import { Typography } from "./typography";

export const PIN_WIDTH = 1000;
export const PIN_HEIGHT = 1500;

// Unifying photo grade applied to the image layer of every photo template.
// Slight desaturation + contrast + a faint warm tone makes a client's
// mismatched source photos read as one cohesive, editorial set. Subtle by
// design — should never look "filtered". Single source of truth.
export const PHOTO_GRADE = "saturate(0.88) contrast(1.05) sepia(0.08)";

export type ListItem = {
  name: string;
  caption?: string;
  imageUrl?: string;
};

export type ItineraryStop = {
  label?: string;
  name: string;
  caption?: string;
  imageUrl: string;
};

export type VenueInfo = {
  name: string;
  location?: string;
  tagline?: string;
};

export type PinInput = {
  eyebrow?: string;
  title?: string;
  titleAccent?: string;
  subtitle?: string;
  imageUrl?: string;
  brand: string;
  brandLogo?: string;
  // Per-pin override: suppress the logo/monogram entirely on templates that
  // auto-place one (hero/footer marks). Set from the pin editor.
  hideLogo?: boolean;
  // venueBold only: a light tint (hex) derived from the photo's dominant
  // color, washed over the paper-texture band so it echoes the image tones.
  bandTint?: string;
  // Skip the photographic grade (desaturate/sepia) — for GRAPHIC images like
  // template mockups that must keep their designed colors.
  rawPhoto?: boolean;
  // Horizontal crop focus 0–1 (symmetry axis). Shifts background-position-x so
  // a centered arch/aisle/roof sits centered in the pin. Defaults to 0.5.
  focusX?: number;
  // Vertical crop focus 0–1. Shifts background-position-y so faces/subjects
  // survive the 2:3 crop (0 = top, 1 = bottom). Defaults to 0.5.
  focusY?: number;
  // venueCompare only: the two labeled halves (e.g. the client's own photo
  // labeled "Barn" vs a licensed stock photo labeled "Ballroom").
  compare?: { imageA: string; labelA: string; imageB: string; labelB: string };
  // venueCompare style: "a" = clean "X vs Y" (default); "b" = a top question
  // ("Where will you say 'I do'?") with an "or" badge instead of "vs".
  compareVariant?: "a" | "b";
  // venueChecklist marker style. "checkbox" (default, real to-do lists) ·
  // "dot" / "number" / "none" for listicles that aren't actually checkable.
  bulletStyle?: "checkbox" | "dot" | "number" | "none";
  // Optional override for the variant-B top question.
  compareQuestion?: string;
  // Render the logo in its original colors instead of the default monochrome
  // white treatment (brightness(0) invert(1)). For colored/detailed marks —
  // wax seals, crests, full-color emblems — that the whitening would flatten
  // to a solid silhouette. venueOverlay / venueCentered only.
  logoFullColor?: boolean;
  cta?: string;
  palette: Palette;
  typography: Typography;
  items?: ListItem[];
  // venueChecklist only: grouped sections (heading + items). When present the
  // checklist renders timeline-style sections; otherwise it uses flat items[].
  sections?: { heading: string; items: string[] }[];
  // venueListCollage only: "labeled" (default) shows a number + name on each
  // tile; "title" hides per-tile labels and overlays one large title across the
  // collage (the A/B variant).
  collageStyle?: "labeled" | "title";
  // venueListCollage "title" style only: "light" (default when unset) = white
  // text with a thick black outline, lines pulled into a tight centered block;
  // "dark" = black text with a white outline, lines spread evenly over the
  // full mosaic (the legacy look).
  collageTitleStyle?: "dark" | "light";
  // Optional override for the primary text color on the text-on-light templates
  // (collage box, numbered collage, checklist, cost). Defaults to near-black.
  textColor?: string;
  stops?: ItineraryStop[];
  venue?: VenueInfo;
  // venueFeature only: which end the text card sits on. Defaults to
  // "bottom". Set to "top" when the photo's subject is in the lower third
  // so the card covers the empty sky/ceiling instead of the subject.
  cardPosition?: "top" | "bottom";
  // No-logo monogram fallback options (venueOverlay / venueCentered):
  //   monogramText — overrides the auto-derived initials.
  //   monogramFont — "serif" (default) or "script".
  monogramText?: string;
  monogramFont?: "serif" | "script";
};

export type TemplateName = "listPin" | "venueFeature" | "carousel" | "venueOverlay" | "venueCentered" | "venueCard" | "boardCover" | "venueBold" | "venueCompare" | "venueFramed" | "venueScript" | "venueCollage" | "venueList" | "venueListCollage" | "venueChecklist" | "venueCost" | "venueTrend";

export type Template = (input: PinInput) => string;

// Light pastel fills keyed to palette/color words in a title. Multi-word keys
// first so "dusty blue" wins over "blue". Shared by the collage templates so a
// palette/color-theme pin picks up a matching tint automatically.
export const PALETTE_TINTS: Record<string, string> = {
  "dusty blue": "#dce6ec", "sage green": "#dde6d6",
  sage: "#dde6d6", greenery: "#dde6d6", green: "#dde6d6",
  blush: "#f7e4e4", rose: "#f7e4e4", pink: "#f7e4e4",
  terracotta: "#f1ded0", rust: "#f1ded0", copper: "#f1ded0",
  champagne: "#f4ecdb", gold: "#f4ecdb", ivory: "#f4ecdb", cream: "#f4ecdb",
  lavender: "#e8e3f1", lilac: "#e8e3f1", purple: "#e8e3f1",
  navy: "#dce6ec", blue: "#dce6ec",
  burgundy: "#ecd9da", wine: "#ecd9da", marsala: "#ecd9da",
  emerald: "#d8e6df", teal: "#d8e6df",
  peach: "#f7e6da", coral: "#f7e6da",
  mauve: "#ecdfe4", dusty: "#ecdfe4",
  mustard: "#f6efd2", yellow: "#f6efd2",
};

// Return the pastel tint for the first palette word found in `text`, else
// `fallback`.
export function paletteTint(text: string, fallback: string): string {
  const hay = (text || "").toLowerCase();
  for (const [k, v] of Object.entries(PALETTE_TINTS)) if (hay.includes(k)) return v;
  return fallback;
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Safe to drop inside CSS `url('...')` within a <style> block. Unlike
// escapeHtml, it must NOT turn & into &amp; — entities aren't decoded inside
// <style>, so that would corrupt query strings (e.g. Storage URLs ending in
// ?alt=media&token=… → black image). It only neutralizes characters that could
// break out of the url('...') quotes.
export function cssUrl(s: string): string {
  return String(s ?? "").replace(/["'()\\\s]/g, encodeURIComponent);
}

// Relative luminance of a #RRGGBB hex (NaN for anything else). Shared by the
// templates' dark-palette / logo-filter checks and the readableOn guard.
export function luminance(hex: string): number {
  const h = String(hex ?? "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return NaN;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Contrast guard for slots that put palette text on palette backgrounds.
// Named palettes are hand-tuned, but B2B clients overlay ARBITRARY brand
// hexes — light-on-light ink/soft combos would otherwise render unreadable
// pins. Keeps `preferred` when it has enough luminance separation from the
// background; otherwise falls back to a dark or light ink picked to contrast.
// Non-hex inputs (rgba, gradients) are left alone — we can't judge them.
export function readableOn(
  bg: string,
  preferred: string,
  dark = "#2A2622",
  light = "#FFF9F2",
): string {
  const lb = luminance(bg);
  const lp = luminance(preferred);
  if (Number.isNaN(lb) || Number.isNaN(lp)) return preferred;
  if (Math.abs(lb - lp) >= 0.35) return preferred;
  return lb > 0.5 ? dark : light;
}

export function renderTitle(title: string, accent: string | undefined): string {
  if (!accent) return escapeHtml(title);
  const a = accent.trim();
  if (!a) return escapeHtml(title);
  // Case-insensitive lookup on the RAW title (so quote extension below sees real
  // quote characters, not HTML entities). Preserves original casing.
  const idx = title.toLowerCase().indexOf(a.toLowerCase());
  if (idx === -1) return escapeHtml(title);
  let start = idx;
  let end = idx + a.length;
  // Swallow quotes immediately around the accent so they render in the accent
  // style too — e.g. accent "I do" in «Say 'I do' at X» highlights 'I do' with
  // its quotes, instead of orphaning them in the surrounding caps.
  const QUOTES = "'\"‘’“”′″`";
  while (start > 0 && QUOTES.includes(title[start - 1])) start--;
  while (end < title.length && QUOTES.includes(title[end])) end++;
  return escapeHtml(title.slice(0, start))
    + `<em class="accent">${escapeHtml(title.slice(start, end))}</em>`
    + escapeHtml(title.slice(end));
}

export function requireImageUrl(input: PinInput, template: string): string {
  if (!input.imageUrl) throw new Error(`${template} template requires imageUrl`);
  return input.imageUrl;
}

export function requireTitle(input: PinInput, template: string): string {
  if (!input.title) throw new Error(`${template} template requires title`);
  return input.title;
}

// Derive a monogram from a business name for the no-logo fallback: first
// letter of each significant word (skipping articles/conjunctions), up to
// three letters. "Cambium Farms" → "CF", "The Glasshouse" → "G",
// "White Willow Events" → "WWE". Rendered as a ringed monogram so a
// logo-less client still gets a clean mark (and a logo-design upsell hook).
export function brandInitials(name: string): string {
  const stop = new Set(["the", "and", "of", "at", "a", "an", "co", "inc", "ltd"]);
  const words = (name || "")
    .split(/\s+/)
    .map((w) => w.replace(/[^A-Za-z]/g, ""))
    .filter((w) => w && !stop.has(w.toLowerCase()));
  const letters = words.map((w) => w[0].toUpperCase()).join("").slice(0, 3);
  return letters || (name || "?").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "?";
}
